// src/lib/kinema/konva/renderers/ShapeRenderer.ts
import Konva from 'konva';
import type { KonvaRenderer } from './types';
import type { BaseLayerData } from '../../core/types';
import { pathPointsToSVG } from '../../core/tools/pathUtils';

function derivePathData(layer: BaseLayerData): string {
  if (layer.pathPoints) return pathPointsToSVG(layer.pathPoints, layer.pathClosed ?? false);
  return '';
}

/**
 * Apply stroke trim (draw-on effect) via dash/dashOffset.
 * trimStart/trimEnd are 0-1 values representing the visible portion of the stroke.
 */
function applyTrim(node: Konva.Node, layer: BaseLayerData): void {
  const trimStart = layer.trimStart ?? 0;
  const trimEnd = layer.trimEnd ?? 1;

  if (trimStart === 0 && trimEnd === 1) {
    // No trim — clear any dash settings
    node.setAttr('dash', undefined);
    node.setAttr('dashOffset', 0);
    return;
  }

  // Estimate path length from the node. Konva.Path has getLength().
  const pathNode = node as any;
  const totalLength = typeof pathNode.getLength === 'function' ? pathNode.getLength() : 0;
  if (totalLength <= 0) return;

  const visibleStart = trimStart * totalLength;
  const visibleLength = (trimEnd - trimStart) * totalLength;
  const gapLength = totalLength - visibleLength;

  // dash pattern: [visible, gap] — dashOffset shifts the start
  node.setAttr('dash', [visibleLength, gapLength]);
  node.setAttr('dashOffset', -visibleStart);
}

export const ShapeRenderer: KonvaRenderer = {
  create(layer: BaseLayerData): Konva.Node {
    switch (layer.shape) {
      case 'ellipse': {
        const shape = new Konva.Shape({
          width: layer.width,
          height: layer.height,
          fill: layer.fill,
          stroke: layer.stroke,
          strokeWidth: layer.strokeWidth,
          sceneFunc: (ctx: any, s: any) => {
            const w = s.width();
            const h = s.height();
            ctx.beginPath();
            ctx.ellipse(w / 2, h / 2, w / 2, h / 2, 0, 0, Math.PI * 2);
            ctx.closePath();
            ctx.fillStrokeShape(s);
          },
          hitFunc: (ctx: any, s: any) => {
            const w = s.width();
            const h = s.height();
            ctx.beginPath();
            ctx.ellipse(w / 2, h / 2, w / 2, h / 2, 0, 0, Math.PI * 2);
            ctx.closePath();
            ctx.fillStrokeShape(s);
          },
        });
        shape.getSelfRect = () => ({ x: 0, y: 0, width: shape.width(), height: shape.height() });
        return shape;
      }
      case 'path': {
        const path = new Konva.Path({
          data: derivePathData(layer),
          fill: layer.fill,
          stroke: layer.stroke,
          strokeWidth: layer.strokeWidth,
        });
        applyTrim(path, layer);
        return path;
      }
      case 'rect':
      default:
        return new Konva.Rect({
          width: layer.width,
          height: layer.height,
          fill: layer.fill,
          stroke: layer.stroke,
          strokeWidth: layer.strokeWidth,
          cornerRadius: layer.cornerRadius,
        });
    }
  },

  update(node: Konva.Node, layer: BaseLayerData, changed: Record<string, boolean>): void {
    if (changed.fill) node.setAttr('fill', layer.fill);
    if (changed.stroke) node.setAttr('stroke', layer.stroke);
    if (changed.strokeWidth) node.setAttr('strokeWidth', layer.strokeWidth);
    if (changed.width) node.setAttr('width', layer.width);
    if (changed.height) node.setAttr('height', layer.height);
    if (changed.cornerRadius) node.setAttr('cornerRadius', layer.cornerRadius);
    if (changed.pathPoints || changed.pathClosed) node.setAttr('data', derivePathData(layer));
    if (changed.trimStart || changed.trimEnd || changed.pathPoints) applyTrim(node, layer);
  },

  destroy(node: Konva.Node): void {
    node.destroy();
  },
};
